import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Search, ArrowLeft, Upload, Building2 } from "lucide-react";
import Swal from "sweetalert2";
import config from "../config";

export default function WebsiteInfoPage({ handlePageChange }) {
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);

  const [form, setForm] = useState({
    id: null,
    companyName: "",
    email: "",
    phone: "",
    office1: "",
    office2: "",
    facebook: "",
    viber: "",
    logoFile: null,
    logo: "",
    isActive: true,
  });

  const API = `${config.API_BASE_URL}/WebsiteInfo`;

  // ===== GET =====
  const loadData = async () => {
    try {
      const res = await fetch(API);
      if (!res.ok) throw new Error();
      setData(await res.json());
    } catch {
      Swal.fire("Error", "Failed to load website info", "error");
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // ===== HANDLE LOGO UPLOAD =====
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // ✅ Validate file type
      if (!file.type.startsWith('image/')) {
        Swal.fire("Error", "Please upload an image file", "error");
        return;
      }

      // ✅ Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        Swal.fire("Error", "File size must be less than 5MB", "error");
        return;
      }

      setForm({ ...form, logoFile: file });
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // ===== SAVE (POST/PUT) =====
  const save = async () => {
    try {
      // ✅ Validation
      if (!form.companyName?.trim()) {
        Swal.fire("Error", "Company name is required", "error");
        return;
      }

      const formData = new FormData();
      formData.append("companyName", form.companyName || "");
      formData.append("email", form.email || "");
      formData.append("phone", form.phone || "");
      formData.append("office1", form.office1 || "");
      formData.append("office2", form.office2 || "");
      formData.append("facebook", form.facebook || "");
      formData.append("viber", form.viber || "");
      formData.append("isActive", form.isActive);

      if (form.logoFile) {
        formData.append("logoFile", form.logoFile);
      }

      const method = form.id ? "PUT" : "POST";
      const url = form.id ? `${API}/${form.id}` : API;

      const res = await fetch(url, {
        method,
        body: formData,
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(errorText || "Failed to save");
      }

      Swal.fire({
        icon: "success",
        title: form.id ? "Updated!" : "Created!",
        timer: 1200,
        showConfirmButton: false,
      });

      closeModal();
      loadData();
    } catch (error) {
      console.error("Save error:", error);
      Swal.fire("Error", error.message || "Failed to save data", "error");
    }
  };

  // ===== DELETE =====
  const remove = async (id) => {
    const confirm = await Swal.fire({
      title: "Delete website info?",
      text: "This action cannot be undone",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      confirmButtonText: "Delete",
    });

    if (!confirm.isConfirmed) return;

    try {
      const res = await fetch(`${API}/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error();

      Swal.fire({
        icon: "success",
        title: "Deleted!",
        timer: 1000,
        showConfirmButton: false,
      });

      loadData();
    } catch {
      Swal.fire("Error", "Failed to delete", "error");
    }
  };

  const openCreate = () => {
    setForm({
      id: null,
      companyName: "",
      email: "",
      phone: "",
      office1: "",
      office2: "",
      facebook: "",
      viber: "",
      logoFile: null,
      logo: "",
      isActive: true,
    });
    setLogoPreview(null);
    setShowModal(true);
  };

  const openEdit = (item) => {
    setForm({ ...item, logoFile: null });
    // ✅ Set logo preview from base64
    setLogoPreview(item.logo ? `data:image/png;base64,${item.logo}` : null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setLogoPreview(null);
  };

  // ===== SEARCH =====
  const filteredData = data.filter(
    (x) =>
      x.companyName?.toLowerCase().includes(search.toLowerCase()) ||
      x.email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-slate-100 p-6">
      {/* HEADER */}
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => handlePageChange("maintenance")}
              className="p-2.5 rounded-xl bg-slate-800/80 hover:bg-slate-700 border border-slate-700/50 transition-all"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                Website Information
              </h1>
              <p className="text-slate-400 text-sm mt-1">Manage company details and contact info</p>
            </div>
          </div>

          <button
            onClick={openCreate}
            className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 px-5 py-2.5 rounded-xl font-medium shadow-lg shadow-blue-500/30 transition-all"
          >
            <Plus size={20} />
            Add New
          </button>
        </div>

        {/* SEARCH */}
        <div className="mb-6 relative w-80">
          <Search className="absolute left-4 top-3.5 text-slate-400" size={18} />
          <input
            className="w-full pl-12 pr-4 py-3 bg-slate-800/60 border border-slate-700/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all"
            placeholder="Search company or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* TABLE */}
        <div className="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 overflow-hidden shadow-2xl">
          <table className="w-full">
            <thead className="bg-slate-800/80 border-b border-slate-700/50">
              <tr>
                <th className="p-4 text-left text-sm font-semibold text-slate-300">Logo</th>
                <th className="p-4 text-left text-sm font-semibold text-slate-300">Company</th>
                <th className="p-4 text-left text-sm font-semibold text-slate-300">Email</th>
                <th className="p-4 text-left text-sm font-semibold text-slate-300">Phone</th>
                <th className="p-4 text-center text-sm font-semibold text-slate-300">Status</th>
                <th className="p-4 text-center text-sm font-semibold text-slate-300">Actions</th>
              </tr>
            </thead>

            <tbody>
              {filteredData.map((item) => (
                <tr
                  key={item.id}
                  className="border-b border-slate-700/30 hover:bg-slate-700/30 transition-colors"
                >
                  <td className="p-4">
                    {item.logo ? (
                      <img
                        src={`data:image/png;base64,${item.logo}`}
                        alt="Logo"
                        className="w-12 h-12 rounded-lg object-cover border border-slate-600"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-slate-700 flex items-center justify-center">
                        <Building2 size={20} className="text-slate-400" />
                      </div>
                    )}
                  </td>
                  <td className="p-4 font-medium">{item.companyName}</td>
                  <td className="p-4 text-slate-300">{item.email}</td>
                  <td className="p-4 text-slate-300">{item.phone}</td>
                  <td className="p-4 text-center">
                    {item.isActive ? (
                      <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-xs font-medium">
                        Active
                      </span>
                    ) : (
                      <span className="px-3 py-1 bg-red-500/20 text-red-400 rounded-full text-xs font-medium">
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="p-4">
                    <div className="flex justify-center gap-3">
                      <button
                        onClick={() => openEdit(item)}
                        className="p-2 rounded-lg bg-yellow-500/10 text-yellow-400 hover:bg-yellow-500/20 transition-all"
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        onClick={() => remove(item.id)}
                        className="p-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-all"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {filteredData.length === 0 && (
                <tr>
                  <td colSpan="6" className="text-center p-12 text-slate-400">
                    <Building2 size={48} className="mx-auto mb-3 opacity-30" />
                    <p className="text-lg">No data found</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* MODAL */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-2xl w-full max-w-2xl p-6 relative shadow-2xl border border-slate-700/50 max-h-[90vh] overflow-y-auto">
            <button
              onClick={closeModal}
              className="absolute right-4 top-4 p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-all"
            >
              <X size={20} />
            </button>

            <h2 className="text-2xl font-bold mb-6 bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
              {form.id ? "Update Website Info" : "Create Website Info"}
            </h2>

            {/* LOGO UPLOAD */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-300 mb-3">
                Company Logo <span className="text-slate-500">(Max 5MB)</span>
              </label>
              <div className="flex items-center gap-4">
                <div className="w-24 h-24 rounded-xl bg-slate-900 border-2 border-dashed border-slate-600 flex items-center justify-center overflow-hidden">
                  {logoPreview ? (
                    <img src={logoPreview} alt="Preview" className="w-full h-full object-cover" />
                  ) : (
                    <Upload size={28} className="text-slate-500" />
                  )}
                </div>
                <div className="flex-1">
                  <label className="cursor-pointer block">
                    <div className="px-4 py-3 bg-slate-900 border border-slate-700 rounded-xl hover:border-blue-500 transition-all text-center">
                      <span className="text-blue-400">Choose file...</span>
                      <p className="text-xs text-slate-500 mt-1">PNG, JPG, GIF (max 5MB)</p>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoChange}
                      className="hidden"
                    />
                  </label>
                  {form.logoFile && (
                    <p className="text-sm text-slate-400 mt-2">
                      Selected: {form.logoFile.name}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* FORM INPUTS */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Company Name <span className="text-red-400">*</span>
                </label>
                <input
                  className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all"
                  placeholder="Company Name"
                  value={form.companyName}
                  onChange={(e) => setForm({ ...form, companyName: e.target.value })}
                />
              </div>

              {[
                ["email", "Email"],
                ["phone", "Phone"],
                ["office1", "Office 1"],
                ["office2", "Office 2"],
                ["facebook", "Facebook"],
                ["viber", "Viber"],
              ].map(([key, label]) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    {label}
                  </label>
                  <input
                    className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all"
                    placeholder={label}
                    value={form[key]}
                    onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  />
                </div>
              ))}

              <div className="col-span-2">
                <label className="flex items-center gap-3 p-4 bg-slate-900 rounded-xl border border-slate-700 cursor-pointer hover:border-blue-500 transition-all">
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                    className="w-5 h-5 rounded bg-slate-800 border-slate-600 text-blue-600 focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="font-medium">Active Status</span>
                </label>
              </div>
            </div>

            {/* BUTTONS */}
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-700">
              <button
                onClick={closeModal}
                className="px-5 py-2.5 rounded-xl bg-slate-700 hover:bg-slate-600 font-medium transition-all"
              >
                Cancel
              </button>
              <button
                onClick={save}
                className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 font-medium shadow-lg shadow-blue-500/30 transition-all"
              >
                {form.id ? "Update" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}